/**
 * SelfVerifyPanel — optional in-flow identity verification (AML-Sumsub-2).
 *
 * Renders in the apply flow's review step and lets the logged-in applicant
 * verify their own identity via Sumsub. It self-gates: if the backend reports
 * Sumsub is not configured, the component renders NOTHING — so the live client
 * apply flow is completely unchanged in production until the keys are set.
 *
 * It is intentionally non-blocking in this phase (does not gate submission);
 * gating on verification is a later policy/rollout decision.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, Loader2 } from "lucide-react";
import { launchSumsub } from "@/lib/sumsub";

export default function SelfVerifyPanel({ API }) {
  const [configured, setConfigured] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/aml/config`)
      .then((res) => { if (active) setConfigured(Boolean(res.data?.sumsub_configured)); })
      .catch(() => { if (active) setConfigured(false); });
    return () => { active = false; };
  }, [API]);

  if (!configured) return null;

  const start = async () => {
    setLaunching(true);
    try {
      const res = await axios.post(`${API}/aml/self/verify-token`);
      setStarted(true);
      setTimeout(() => {
        launchSumsub({
          containerId: "self-sumsub-mount",
          tokenInfo: res.data,
          onError: (e) => toast.error((e && e.reason) || "Verification error"),
        }).catch((e) => toast.error(e.message || "Could not launch verification"));
      }, 150);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start verification");
    } finally {
      setLaunching(false);
    }
  };

  return (
    <section className="form-card rounded border border-[#bd9a59]/30 bg-[#bd9a59]/5">
      <h4 className="section-subheader flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-[#bd9a59]" /> Verify your identity
      </h4>
      <p className="text-sm text-gray-600 mb-3">
        Complete a quick identity check (photo ID + selfie). This helps us meet our
        compliance obligations and can speed up your application.
      </p>
      {!started && (
        <button
          type="button"
          onClick={start}
          disabled={launching}
          className="btn-accent px-4 py-2 rounded text-sm flex items-center gap-2 disabled:opacity-50"
        >
          {launching ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
          Start verification
        </button>
      )}
      <div id="self-sumsub-mount" className="mt-3" />
    </section>
  );
}
